<template>
  <div>
    <!-- Hidden trigger button for programmatic opening -->
    <button ref="triggerButton" style="display: none">
      Trigger Feedback Widget
    </button>

    <!-- Custom feedback button (optional) -->
    <button
      v-if="showCustomButton"
      class="featurebase-custom-button"
      @click="openWidget"
    >
      <slot name="button">
        {{ buttonText }}
      </slot>
    </button>
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, onMounted, ref } from "vue";

import type { Controller } from "@ctrl/controller-rpc";

import { useProfile } from "@/composables/useProfile";
import { property } from "@/utils/unknown";

interface Props {
  organization: string;
  theme?: "light" | "dark" | "auto";
  placement?: "left" | "right";
  email?: string;
  defaultBoard?: string;
  locale?: string;
  metadata?: Record<string, unknown>;
  showCustomButton?: boolean;
  buttonText?: string;
  controller?: Controller | null;
}

const props = withDefaults(defineProps<Props>(), {
  theme: "auto",
  locale: "en",
  showCustomButton: false,
  buttonText: "Open Widget",
  controller: null,
});

const controller = props.controller;
const triggerButton = ref<HTMLButtonElement>();

// Load the Featurebase SDK script
onBeforeMount(() => {
  // Check if script is already loaded
  if (document.getElementById("featurebase-sdk")) {
    return;
  }

  const script = document.createElement("script");
  script.src = "https://do.featurebase.app/js/sdk.js";
  script.id = "featurebase-sdk";
  script.async = true;
  document.head.appendChild(script);
});

onMounted(async () => {
  // Wait for the script to load
  await new Promise((resolve) => {
    const checkScript = () => {
      if ("Featurebase" in window) {
        resolve(true);
      } else {
        setTimeout(checkScript, 100);
      }
    };
    checkScript();
  });

  let featurebase = property(window, "Featurebase");

  // Initialize Featurebase queue if not already initialized
  if (typeof featurebase !== "function") {
    const queue: unknown[] = [];
    featurebase = Object.assign(
      (...args: unknown[]) => {
        queue.push(args);
      },
      { q: queue }
    );
    Reflect.set(window, "Featurebase", featurebase);
  }
  if (typeof featurebase !== "function") return;

  // Get user profile for email and metadata
  let userEmail = props.email;
  let userMetadata = props.metadata;

  if (controller && !userEmail) {
    try {
      const profile = await useProfile(controller);
      profile.release();
      if (profile.value?.user.email) {
        userEmail = profile.value.user.email;
        userMetadata = {
          ...userMetadata,
          userId: profile.value.user.accountId,
          userName:
            `${profile.value.user.givenName} ${profile.value.user.familyName}`.trim(),
        };
      }
    } catch (error) {
      window.console.warn("Failed to get user profile for Featurebase:", error);
    }
  }

  // Determine theme based on current app theme
  let theme = props.theme;
  if (theme === "auto") {
    // Check if dark theme is active
    const isDark = document.documentElement.classList.contains("theme--dark");
    theme = isDark ? "dark" : "light";
  }

  // Initialize the feedback widget
  const initConfig: {
    organization: string;
    theme: "light" | "dark" | "auto";
    email?: string;
    locale: string;
    metadata?: Record<string, unknown>;
    placement?: "left" | "right";
    defaultBoard?: string;
  } = {
    organization: props.organization,
    theme: theme,
    email: userEmail,
    locale: props.locale,
    metadata: userMetadata,
  };

  // Only include placement if explicitly provided (to hide floating button by default)
  if (props.placement) {
    initConfig.placement = props.placement;
  }

  // Only include defaultBoard if provided
  if (props.defaultBoard) {
    initConfig.defaultBoard = props.defaultBoard;
  }

  featurebase(
    "initialize_feedback_widget",
    initConfig,
    (err: unknown, callback: unknown) => {
      if (err) {
        window.console.warn("Featurebase init callback error:", err);
        return;
      }
      // Forward widget events to the app so we can react (e.g. trigger Sentry upload on submit).
      // Docs mention actions like: widgetReady, widgetOpened, feedbackSubmitted.
      window.dispatchEvent(
        new CustomEvent("featurebase:widget", {
          detail: callback,
        })
      );
    }
  );

  // Set the data attribute on the trigger button for programmatic opening
  if (triggerButton.value) {
    triggerButton.value.setAttribute("data-featurebase-feedback", "");
  }
});

const openWidget = () => {
  // Trigger the widget to open by clicking the hidden button
  if (triggerButton.value) {
    triggerButton.value.click();
  }
};
</script>

<style scoped lang="scss">
.featurebase-custom-button {
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: 7px;
  border-width: 1px;
  border-style: solid;
  @extend %font-body-regular;
  padding: 4px 12px 5px 12px;
  @include transition(all 100ms ease);
  transition-property: color, background-color, border;

  @include themed() {
    background: t($bg-base-button);
    color: t($grey-80);
    border-color: t($border-button);
  }

  &:hover {
    @include themed() {
      background: t($grey-10);
      color: t($primary);
      border-color: t($grey-50);
    }
  }

  &:active {
    @include themed() {
      background: t($grey-15);
      color: t($grey-80);
    }
  }
}
</style>
